import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ScanSearch } from "lucide-react";
import SpotlightCard from "@/components/SpotlightCard";
import CTABanner from "@/components/CTABanner";
import { Reveal, Eyebrow, MaskLines } from "@/components/Reveal";

const filters = ["All", "NDE Methods", "Auditing & QA/QC", "Field & Project Support"];

const services = [
  { cat: "NDE Methods", code: "NDE-01", title: "Visual Testing (VT)", desc: "Direct and remote visual inspection of welds, components, and assemblies — the first and most fundamental line of defect detection, executed with disciplined technique." },
  { cat: "NDE Methods", code: "NDE-02", title: "Magnetic Particle Testing (MT)", desc: "Surface and near-surface discontinuity detection in ferromagnetic materials, from structural welds to critical rotating equipment components." },
  { cat: "NDE Methods", code: "NDE-03", title: "Liquid Penetrant Testing (PT)", desc: "Sensitive surface-breaking defect detection on non-porous materials — stainless, aluminum, and alloys across piping, vessels, and machined parts." },
  { cat: "NDE Methods", code: "NDE-04", title: "Ultrasonic Testing (UT)", desc: "High-frequency sound wave interrogation for internal flaws, thickness measurement, and corrosion mapping on critical assets." },
  { cat: "NDE Methods", code: "NDE-05", title: "Phased Array Ultrasonic Testing (PAUT)", desc: "Advanced multi-element beam steering for detailed weld examination, encoded scanning, and imaging that leaves a permanent, auditable record." },
  { cat: "NDE Methods", code: "NDE-06", title: "Radiographic Film Auditing", desc: "Independent second-reader review and audit of radiographic film — verifying interpretation quality, technique, and code compliance." },
  { cat: "Auditing & QA/QC", code: "QA-01", title: "NDE Auditing — Level III Led", desc: "Our specialty. ASNT Level III–led audits of NDE programs, personnel qualification, technique sheets, and field execution against SNT-TC-1A, CP-189, and client specifications." },
  { cat: "Auditing & QA/QC", code: "QA-02", title: "QA/QC Program Support", desc: "Quality program development, surveillance, and hands-on QC support that keeps fabrication and construction aligned with specification." },
  { cat: "Auditing & QA/QC", code: "QA-03", title: "Procedure Verification", desc: "Review and verification of NDE procedures, WPS/PQRs, and inspection test plans against ASME, API, AWS, and ISO requirements." },
  { cat: "Auditing & QA/QC", code: "QA-04", title: "Compliance & Documentation Review", desc: "Systematic review of quality records, turnover packages, and traceability documentation — built to survive regulatory and owner audits." },
  { cat: "Field & Project Support", code: "FS-01", title: "Field Inspection Support", desc: "Embedded inspectors and technical specialists supporting your team where the work happens — plants, right-of-ways, and job sites." },
  { cat: "Field & Project Support", code: "FS-02", title: "Project Coordination", desc: "Coordination between owners, contractors, and inspection resources to keep quality activities sequenced and on schedule." },
  { cat: "Field & Project Support", code: "FS-03", title: "Contractor Oversight", desc: "Owner-side oversight of contractor NDE and quality performance — objective verification that the work matches the paperwork." },
  { cat: "Field & Project Support", code: "FS-04", title: "Safety & Quality Monitoring", desc: "Continuous field monitoring of safety practices and quality execution, with findings escalated before they become incidents or rework." },
  { cat: "Field & Project Support", code: "FS-05", title: "Client Representation", desc: "APX acts as your technical representative on site — protecting your interests in every inspection hold point and quality decision." },
  { cat: "Field & Project Support", code: "FS-06", title: "Project Tracking & Documentation", desc: "Structured tracking of inspection status, NCRs, and completion metrics with disciplined document control throughout the project lifecycle." },
  { cat: "Field & Project Support", code: "FS-07", title: "Field Reporting", desc: "Clear, timely, defensible field reports — daily progress, inspection results, and audit findings delivered in formats your team can act on." },
];

const codes = ["ASME", "API", "AWS", "ISO", "ASNT SNT-TC-1A", "CP-189"];

export default function Services() {
  const [active, setActive] = useState("All");
  useEffect(() => {
    document.title = "Services | APX Project Solutions LLC";
  }, []);

  const visible = active === "All" ? services : services.filter((s) => s.cat === active);

  return (
    <div data-testid="services-page">
      <section className="tech-grid relative overflow-hidden border-b border-white/[0.08]" data-testid="services-hero">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Eyebrow>Services — Full Capability Index</Eyebrow>
          <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <MaskLines lines={[<>Every method.</>, <span className="text-apx">One standard.</span>]} />
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
            From Level III–led NDE auditing to embedded field support, APX delivers the full inspection and quality stack — with documentation that holds up under any scrutiny.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24" data-testid="services-index">
        <div className="flex flex-wrap gap-3" data-testid="services-filter-bar">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`chamfer-sm px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                active === f ? "bg-apx text-ink" : "border border-white/15 text-zinc-400 hover:border-apx hover:text-apx"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-12 grid gap-px overflow-hidden border border-white/[0.08] bg-white/[0.08] md:grid-cols-2">
          {visible.map((s) => (
            <SpotlightCard key={s.code} className="group h-full bg-ink p-8 transition-colors hover:bg-surface" data-testid={`service-detail-${s.code.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-apx">{s.code}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">{s.cat}</span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
            </SpotlightCard>
          ))}
        </div>
      </section>

      <section className="border-y border-white/[0.08] bg-coal py-20 sm:py-24" data-testid="services-level3">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <Eyebrow>Signature Capability</Eyebrow>
            <h2 className="mt-6 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl">
              NDE auditing by Level III — our core discipline
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">
              Anyone can point a probe. APX audits the entire system behind the result: personnel qualification, procedure compliance, technique execution, and interpretation quality. Led by ASNT Level III expertise, our audits give owners and EPCs an independent, defensible picture of their NDE program — before a regulator or a failure does.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="chamfer border border-apx/30 bg-ink p-8">
              <ScanSearch className="h-8 w-8 text-apx" strokeWidth={1.5} />
              <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-white">Codes &amp; Standards We Work To</h3>
              <div className="mt-5 flex flex-wrap gap-2">
                {codes.map((c) => (
                  <span key={c} data-testid={`code-badge-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="chamfer-sm border border-white/15 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-300">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24" data-testid="services-cta-strip">
        <Reveal className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <h2 className="max-w-xl font-display text-3xl font-black uppercase leading-tight tracking-tight text-white sm:text-4xl">
            Not sure which method your scope requires?
          </h2>
          <Link
            to="/contact"
            data-testid="services-contact-link"
            className="chamfer-sm group inline-flex items-center gap-2 bg-apx px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-white"
          >
            Ask APX <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>

      <CTABanner />
    </div>
  );
}
